<template>
    <div class="media-location" v-if="item.hasCameraPosition">
        <h4 class="m-t-10 m-b-10 header-title">Location</h4>
        <mapbox-map
            map-id='map'
            :map-zoom="mapZoom"
            :minZoom="12"
            :maxZoom="16"
            :center="cameraLocation"
            :reset="reloadMap"
            :map-style="mapStyle"
            v-on:map-loaded="onLoad($event)"
            v-on:map-reset="reloadMap = false">

            <button class="btn btn-primary btn-sm btn-map-reset" @click="reloadMap = true">
                <i class="fas fa-sync"></i>
            </button>
        </mapbox-map>
    </div>
</template>

<script>
import { has } from 'lodash';
import MapboxMap from '@/components/mapbox/MapboxMap';

// Helper
import { toRad, toDeg } from 'ca-js-helper';

export default {
    name: 'MediaLocation',
    components: {
        MapboxMap,
    },
    props: {
        item: {
            type: Object,
            required: true,
        }
    },
    data() {
        return {
            reloadMap: false,
            disableRefresh: true,
        }
    },
    computed: {
        cameraLocation() {
            return [this.item.cameraPosition.Longitude, this.item.cameraPosition.Latitude];
        },
        mapStyle() {
            return this.useProjectSettings && has(ca.project, 'map_style_url') && ca.project.map_style_url ?
                ca.project.map_style_url : ca.config('media.features.location_map.style');
        },
        useProjectSettings() {
            return ca.config('media.features.location_map.use_project_settings');
        },
        /**
         * @todo: map zoom settings would be an ideal CASCON proof of concept
         * the setting names need cleanup, eg. commentsZoom is indicated as
         * "The default zoom for other maps" in config/ca.php
         */
        mapZoom() {
            return this.useProjectSettings && has(ca.project, 'zoom') && ca.project.zoom ?
                ca.project.zoom : ca.config('media.features.location_map.map_zoom');
        },
    },
    methods: {
        onLoad(map) {
            const cameraLocation = this.cameraLocation;

            if (this.item.hasTargetPosition && !this.item.has360) {

                const targetPosition = this.item.targetPosition;
                const cameraPosition = this.item.cameraPosition;
                // Determine bearing the camera is pointing
                // Extrapolated from https://github.com/Turfjs/turf/blob/a4f8fc17a487e5a63dff9358989e7809a59d8931/packages/turf-bearing/index.js
                // Should be refactored into a function for use globally
                let lon1 = toRad(cameraPosition.Longitude);
                let lon2 = toRad(targetPosition.Longitude);
                let lat1 = toRad(cameraPosition.Latitude);
                let lat2 = toRad(targetPosition.Latitude);
                let a = Math.sin(lon2 - lon1) * Math.cos(lat2);
                let b = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(lon2 - lon1);

                let bearing = toDeg(Math.atan2(a, b));

                // Now load the marker with the frustum as a Mapbox image layer
                // and rotate that image to the bearing calculated above

                map.loadImage('/images/mapbox/marker-frustum-512x512.png', function(error, image) {
                    if (error) throw error;
                    map.addImage('cat', image);
                    map.addLayer({
                        "id": "points",
                        "type": "symbol",
                        "source": {
                            "type": "geojson",
                            "data": {
                                "type": "FeatureCollection",
                                "features": [{
                                    "type": "Feature",
                                    "geometry": {
                                        "type": "Point",
                                        "coordinates": cameraLocation
                                    }
                                }]
                            }
                        },
                        "layout": {
                            "icon-image": "cat",
                            "icon-size": 0.4,
                            "icon-rotate": bearing
                        }
                    });
                });
            } else {
                const marker = new mapboxgl.Marker()
                    .setLngLat(cameraLocation)
                    .addTo(map);
            }

            window.map = map;
            window.pixelDensity = `@${window.devicePixelRatio}x`;
            let oScriptText = null;

            // append custom js
            if (this.useProjectSettings && has(ca.project, 'custom_js') && ca.project.custom_js) {
                oScriptText = document.createTextNode(ca.project.custom_js);
            } else {

                oScriptText = document.createTextNode(`
                    var layerOptions = {
                        "id":"satellite_metromap",
                        "type":"raster",
                        "source":{
                        "type":"raster",
                        "tiles":[
                            "https://api.metromap.com.au/metromapkey/wmts?key=oxl8tBbeDko-4nVlrB647jr7q8VK27gYAYI65i4rNkU=&REQUEST=GetTile&SERVICE=WMTS&LAYER=Australia_latest&FORMAT=image/webp&TileMatrixSet=GoogleMapsCompatibleExt:epsg:3857&TileMatrix={z}&TileRow={y}&TileCol={x}"
                        ],
                        "tileSize":256,
                        "attribution":"&copy; Metromap"
                        },
                        "minzoom":11,
                        "maxzoom":22
                    }

                    map.addLayer(layerOptions, 'tunnel-street-minor-low');
                `);
            }

            if (oScriptText) {
                const oScript = document.createElement('script');
                oScript.appendChild(oScriptText);
                document.body.appendChild(oScript);
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.media-location {
    position: relative;

    #map {
        width: 100%;
        height: 300px;
    }

    .btn-map-reset {
        position: absolute;
        top: 10px;
        right: 10px;
        z-index: 999;
    }

}
</style>
